export { Market, RestResponseKey } from '@leanpay/models'

export enum Environment {
    LOCAL = 'LOCAL',
    TEST = 'TEST',
    STAGE = 'STAGE',
    TILT = 'TILT',
    PROD = 'PROD',
}

export enum Language {
    SLO = 'sl',
    ENG = 'en',
    ROM = 'ro',
    HUN = 'hu',
    CRO = 'hr',
}

export enum CountryCode {
    SLO = 'si',
    CRO = 'hr',
    HUN = 'hu',
    ROM = 'rom',
}

const second = 1000
const minute = 60 * second
const hour = 60 * minute

export enum CacheDuration {
    Short = 15 * minute,
    Medium = 60 * minute,
    Long = 6 * hour,
    UntilInvalidated = Infinity,
}

export enum STORAGE_KEY {
    AUTH_TOKEN = 'AUTH_TOKEN',
    SESSION_INFO = 'SESSION_INFO',
    // Which checkout the stored AUTH_TOKEN belongs to. Both live in localStorage, which
    // every tab of the origin shares, so this is what lets one tab tell "the customer
    // re-opened the checkout I am already in" from "a different checkout is starting".
    ACTIVE_CHECKOUT_TOKEN = 'ACTIVE_CHECKOUT_TOKEN',
}

export const EMAIL_REGEX = /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/

// BE rejects AddressDetailsRequest.houseNumber longer than this with a generic 400,
// so every address form has to cap the field before submit.
export const MAX_STREET_NUMBER_LENGTH = 10

export enum IconName {
    Error = 'Error',
    Info = 'Info',
    Success = 'Success',
    Warning = 'Warning',
    Approved = 'Approved',
    Rejected = 'Rejected',
    Timeout = 'Timeout',

    VerificationStatusError = 'VerificationStatusError',
    VerificationStatusReject = 'VerificationReject',
    VerificationStatusSuccess = 'VerificationSuccess',
    VerificationStatusTimeout = 'VerificationTimeout',
    VerificationStatusWarning = 'VerificationWarning',

    LeanPayTreeIcon = 'LeanPayTreeIcon',
    ShopCartIcon = 'ShopCartIcon',
    LockIcon = 'LockIcon',
    FAQIcon = 'FAQIcon',
    TooltipIcon = 'TooltipIcon',
    CheckboxCheckedIcon = 'CheckboxCheckedIcon',
    PlusCircleIcon = 'PlusCircleIcon',
    CheckIcon = 'CheckIcon',

    CloseCircleIcon = 'CloseCircleIcon',
    CloseIcon = 'CloseIcon',
    CalendarIcon = 'CalendarIcon',
    SmartphoneIcon = 'SmartphoneIcon',
    UserIcon = 'UserIcon',
    QuickCheckIcon = 'QuickCheckIcon',
    PowerByLeanPayIcon = 'PowerByLeanPayIcon',
    Identyum = 'Identyum',
    CheckMarkIcon = 'CheckMarkIcon',
    SisbonLogoIcon = 'SisbonLogoIcon',
    DownloadIcon = 'DownloadIcon',
    DocumentIcon = 'DocumentIcon',
    ContractIcon = 'ContractIcon',
    ContractSearchIcon = 'ContractSearchIcon',
    ArrowIcon = 'ArrowIcon',
    BurgerMenuIcon = 'BurgerMenuIcon',
    MasterCardIcon = 'MasterCardIcon',
    VisaIcon = 'VisaIcon',
    RingIcon = 'RingIcon',
    IdCardIcon = 'IdCardIcon',
    CreditCardIcon = 'CreditCardIcon',
    DashboardUserIcon = 'DashboardUserIcon',
    LeanPayTreeSecondaryIcon = 'LeanPayTreeSecondaryIcon',
    EmailIcon = 'EmailIcon',
    BackIcon = 'BackIcon',

    LogoutIcon = 'LogoutIcon',
    TransactionsIcon = 'TransactionsIcon',
    GreenShieldIcon = 'GreenShieldIcon',
    SwitchCheckIcon = 'SwitchCheckIcon',
    MobilePhoneIcon = 'MobilePhoneIcon',
    ShopBagIcon = 'ShopBagIcon',
    CalculatorIcon = 'CalculatorIcon',
    CheckedIcon = 'CheckedIcon',
    SettingsIcon = 'SettingsIcon',
    SettingsIconSecondary = 'SettingsIconSecondary',
    SpeedyClockIcon = 'SpeedyClockIcon',
    QuestionMarkSelfCheckoutIcon = 'QuestionMarkSelfCheckoutIcon',
    RedirectIcon = 'RedirectIcon',
    SearchIcon = 'SearchIcon',
    QuestionMarkIcon = 'QuestionMarkIcon',
    OffersIcon = 'OffersIcon',
    InvoiceIcon = 'InvoiceIcon',
    FilterIcon = 'FilterIcon',
    UserProfileIcon = 'UserProfileIcon',
    CookieIcon = 'CookieIcon',
    TaskCheckIcon = 'TaskCheckIcon',
    ChevronDownIcon = 'ChevronDownIcon',

    //Logos
    BigBangLogoIcon = 'BigBangLogoIcon',
    AliansaLogoIcon = 'AliansaLogoIcon',
    BralkoLogoIcon = 'BralkoLogoIcon',
    EltusLogoIcon = 'EltusLogoIcon',
    EmudiaLogoIcon = 'EmudiaLogoIcon',
    EplLogoIcon = 'EplLogoIcon',
    IdeoLogoIcon = 'IdeoLogoIcon',
    IstoreLogoIcon = 'IstoreLogoIcon',
    AddikoLogoIcon = 'AddikoLogoIcon',
    GbLogoIcon = 'GbLogoIcon',
    IonLogoIcon = 'IonLogoIcon',
    MedtLogoIcon = 'MedtLogoIcon',
    TrigalLogoIcon = 'TrigalLogoIcon',
    TriglavLogoIcon = 'TriglavLogoIcon',
    IStyleLogoIcon = 'IStyleLogoIcon',
}

export const daysInMonth = Array.from({ length: 31 }, (_, i) => `${i + 1}`).map((d) => {
    return { label: d, value: d }
})

export interface Option {
    label: string
    value: any
}

export interface Testimonial {
    personName: string
    text: string
}

export enum CardPaymentFailureReason {
    AUTHORIZATION_FAILED_3D_SECURE_MANDATORY = 'AUTHORIZATION_FAILED_3D_SECURE_MANDATORY',
    INVALID_CARD = 'INVALID_CARD',
    CVV2_FAILURE = 'CVV2_FAILURE',
    INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
    CARD_TYPE_NOT_SUPPORTED = 'CARD_TYPE_NOT_SUPPORTED',
    REFERENCED_TRANSACTION_FAILED_GENERAL_ERROR = 'REFERENCED_TRANSACTION_FAILED_GENERAL_ERROR',
    // NEW
    INCORRECT_CARD_NUMBER = 'INCORRECT_CARD_NUMBER',
    CARD_EXPIRED = 'CARD_EXPIRED',
    GENERAL_ERROR = 'GENERAL_ERROR',
}

export const sloTestimonials: Testimonial[] = [
    {
        personName: 'Miha Š.',
        text: 'Najboljše je, ker ti ni treba na banko niti nosit dokumentacije.',
    },
    {
        personName: 'Tadej J.',
        text: 'Na netu daš nekaj podatkov in takoj dobiš odobritev.',
    },
    {
        personName: 'Jelena L.',
        text: 'Vse lahko narediš na telefonu v par klikih.',
    },
    {
        personName: 'Matjaž H.',
        text: 'V aplikaciji vidiš vse na enem mestu in veš kaj si že plačal.',
    },
    {
        personName: 'Jerneja S.',
        text: 'Nobenih presenečenj, kar vidiš to dobiš.',
    },
    {
        personName: 'Luka C.',
        text: 'Ko sem se odločil, da vrnem nakup ni bilo problemov.',
    },
    {
        personName: 'Barbara V.',
        text: 'Takoj sem zvedla kak limit lahko dobim.',
    },
    {
        personName: 'Anže H.',
        text: 'Sprobal sem, da vidim kaj lahko dobim in moram rečt, da je zelo enostavno.',
    },
]

export const croTestimonials: Testimonial[] = [
    {
        personName: 'Barbara V.',
        text: 'Odmah sam znala koji limit mi je odobren.',
    },
    {
        personName: 'Luka C.',
        text: 'Kad sam odlučio vratiti robu koju sam kupio, nije bilo problema.',
    },
    {
        personName: 'Ana S.',
        text: 'Nema iznenađenja, ono što vidite to dobijete.',
    },
    {
        personName: 'Jelena L.',
        text: 'Na telefonu možete obaviti sve u nekoliko klikova.',
    },
    {
        personName: 'Matej J.',
        text: 'Unesete podatke u sustav i odmah dobijete odobrenje.',
    },
    {
        personName: 'Mihael Š.',
        text: 'Najbolje je što ne moram nikakve papire nositi u banku.',
    },
    {
        personName: 'Tina V.',
        text: 'Nema dodatnih ni skrivenih troškova, stvarno je kamata 0%.',
    },
    {
        personName: 'Matija H.',
        text: 'U aplikaciji vidite sve na jednom mjestu i znate što ste već platili i koliko možete potrošiti.',
    },
    {
        personName: 'Alen. H',
        text: 'Htio sam provjeriti kako izgleda ova aplikacija, i ugodno sam se iznenadio koliko je jednostavno.',
    },
    {
        personName: 'Ivana P.',
        text: 'Bez papira i kartica, jednostavno i glatko.',
    },
]

//Helper enum for select options in forms (HUN, SLO, ROM)
export enum SelectOptionOther {
    OtherHUN = 'Egyéb, a listában nem szereplő',
    OtherSLO = 'Drugo',
    OtherROM = 'Altul',
}

export enum Screen {
    Mobile = 'Mobile',
    Tablet = 'Tablet',
    Desktop = 'Desktop',
    LargeDesktop = 'LargeDesktop',
}

export enum AreaCode {
    SLO = '386',
    SRB = '381',
    HUN = '36',
    ROM = '40',
}
